import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
import NotFound from "shared/components/UIElements/NotFound";
import RouteLimitBlocker from "components/shared/RouteLimitBlocker";

import MapWithTransferTimesContainer from "components/shared/MapWithTransferTimesContainer";
import CreateOppositeServiceButton from "components/shared/CreateOppositeServiceButton";

// #region Components
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import ServiceDaysPicker from "shared/components/FormElements/ServiceDaysPicker";
import SmsTable from "features/sms/components/SmsTable";
import GenericCard from "components/ui/cards/GenericCard";
import Tabs from "components/ui/tabs/Tabs";
// #endregion Components

import { isArray } from "lodash";

// #region Utils
// import { GetPhoneNumberofStudent, GetParentFromStudent } from "shared/util/relationaldata";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE, VALIDATOR_TIME_EMPTY_ACCEPTED } from "shared/util/validators";
import { COMPANY_FEATURES, PLANS } from "@routebot/constants";
import { useRouteLimit } from "shared/hooks/useRouteLimit";

import { GetStartStopTime } from "shared/util/time";
import { CheckTimeString } from "shared/util/time";
import { checkIsPickUp } from "shared/utility/types";
import {
  isActiveOptions_tr,
  isActiveOptions_en,
  personelServiceRouteTypes_tr,
  personelServiceRouteTypes_en,
  yesNoOptions_tr,
  yesNoOptions_en,
} from "shared/util/types";
// #endregion Utils

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { isPhoneAppAdminUiEnabled } from "config/ConfigUtils";
import { StoreContext } from "shared/context/store-context";
import { getCurrentLanguage } from "shared/utility/lang";

import ServiceAuditHistoryModal from "features/services/shared/ServiceAuditHistoryModal";
import RideSkipRosterPanel, { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/RideSkipRosterPanel";
import { isRideSkipUiEnabled } from "features/ride-skip/constants";

const VehicleRouteItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  //standard code
  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const forceShowMap = true;
  const [showMap, setShowMap] = useState(forceShowMap);
  const showMapRef = useRef(null);

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // context
  const auth = useContext(AuthContext);
  const showPhoneAppAdminUi = isPhoneAppAdminUiEnabled(auth?.company?.features);
  const {
    vehicleRoutes,
    users,
    students,
    studentSchoolOptions,
    driverOptions,
    studentOptions,
    schools,
    drivers,
    GetParentFromStudent,
  } = useContext(StoreContext);

  // Route limit check for new items
  const { limitStatus, isLimitReached, isUnlimited } = useRouteLimit();
  const shouldBlockNewItem = isNewItem && isLimitReached && !isUnlimited && auth?.company?.plan !== PLANS.UNLIMITED;

  // item info
  const itemType = "vehicleRoute";
  const endPointUpdate = "vehicleroutes";
  const endPointDelete = "vehicleroutes";
  const endPointCreate = "vehicleroutes";

  // item special states
  const [currentStudents, setCurrentStudents] = useState([]);
  const [currentDrivers, setCurrentDrivers] = useState([]);

  // item special functions
  // const loadSMS = useCallback(async () => {
  //   setCurrentSmses(await getSMSByVehicleRoute(itemId));
  // }, [getSMSByVehicleRoute, itemId]);

  const updateCurrentStudents = (data) => {
    if (!data) {
      setCurrentStudents([]);
      return;
    }

    let nowStudents = [];

    let checker = isArray(data) ? data : formState.inputs.student.value;

    for (let i = 0; i < checker.length; i++) {
      const studentId = checker[i];
      const student = students.find((item) => item.id === studentId);
      if (!nowStudents.includes(student)) {
        nowStudents.push(student);
      }
    }

    const newStudents = [];

    const currentStudentIds = currentStudents.map((item) => item.id);

    for (let i = 0; i < nowStudents.length; i++) {
      const student = nowStudents[i];

      if (currentStudentIds.includes(student.id)) {
        student.transferTime = currentStudents.find((item) => item.id === student.id).transferTime;
      } else {
        if (loadedItem?.routelocations && loadedItem?.routelocations?.length > 0) {
          const currentRoutelocations = loadedItem.routelocations.find((item) => item?.student === student.id);
          if (currentRoutelocations) {
            student.transferTime = currentRoutelocations.transfertime;
          }
        }
      }
      newStudents.push(student);
    }

    setCurrentStudents(newStudents);
  };

  const toggleMap = () => {
    const newShowMapState = !showMap;
    setShowMap(newShowMapState);
    // Haritayı gösterirken butona scroll yap
    if (newShowMapState) {
      setTimeout(() => {
        showMapRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  const updateDrivers = (updatedDrivers) => {
    setCurrentDrivers(
      drivers
        .filter((item) => updatedDrivers?.includes(item.id.toString()))
        .map((item) => {
          return {
            id: item.id,
            name: item.name,
            phonenumber: item.phonenumber,
          };
        }),
    );
  };

  const [formState, inputHandler] = useForm(
    {
      school: { value: studentSchoolOptions.length > 1 ? "" : studentSchoolOptions[0]?.value || "", isValid: true },
      name: { value: null, isValid: true },
      driver: { value: [], isValid: true },
      plate: { value: "", isValid: true },
      starttime: { value: null, isValid: true },
      stoptime: { value: null, isValid: true },
      student: { value: [], isValid: true },
      transferStartFinishTime: { value: null, isValid: true },
      isActive: { value: true, isValid: true },
      useUsersDetails: { value: true, isValid: true },
      routeType: { value: "", isValid: true },
      serviceDays: { value: [], isValid: true },
    },
    true,
  );

  // #region Standard API calls
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentVehicleRoute = vehicleRoutes.find((vehicleRoute) => vehicleRoute.id === itemId);
      setLoadedItem(currentVehicleRoute);

      if (!currentVehicleRoute) {
        setIsItemLoaded(true);
        return;
      }
      setCurrentDrivers(
        drivers
          .filter((item) =>
            currentVehicleRoute?.driver.map((driverItem) => driverItem.id.toString()).includes(item.id.toString()),
          )
          .map((item) => {
            return {
              id: item.id,
              name: item.name,
              phonenumber: item.phonenumber,
            };
          }),
      );

      setCurrentStudents(
        currentVehicleRoute.student.map((student) => ({
          ...student,
          transferTime:
            currentVehicleRoute?.routelocations?.find((order) => order?.student?.toString() === student?.id?.toString())
              ?.transfertime || null,
        })),
      );

      // loadSMS();
    } catch (err) {}
    setIsItemLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, vehicleRoutes, users, students]);

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  // eslint-disable-next-line
  const itemUpdateSubmitHandler = useCallback(async (event) => {
    event.preventDefault();

    // if (!CheckTimeString(formState.inputs.starttime.value) || !CheckTimeString(formState.inputs.stoptime.value)) {
    //   alertMessageRef.current = "Saat bilgisini **:** formatında giriniz. Örnek: 07:00 ya da 18:35";
    //   setShowAlert(true);
    //   return;
    // }

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    let routelocations = [
      {
        student: "2",
        transfertime: "1",
      },
    ];

    for (let i = 0; i < currentStudents.length; i++) {
      if (currentStudents[i].transferTime) {
        routelocations.push({
          student: currentStudents[i].id,
          transfertime: currentStudents[i].transferTime,
        });
      }
    }

    // Ekrandaki harita toplamlarını oku (tek harita → sabit id'ler güvenli).
    // Her iki metrik de YALNIZCA geçerli sayı okunduğunda gönderilir: harita "-" gösterirken
    // ("Maps hatası) ham "-" göndermek backend'de cast hatasıyla kaydı patlatabilirdi.
    let totalDistance;
    try {
      const rawDistance = document.getElementById("totalDistance")?.textContent?.replace("km", "").trim();
      const parsedDistance = parseFloat(rawDistance);
      totalDistance = Number.isFinite(parsedDistance) ? parsedDistance : undefined;
    } catch (err) {
      totalDistance = undefined;
    }
    let totalDuration;
    try {
      const parsed = parseInt(document.getElementById("totalDuration")?.textContent, 10);
      totalDuration = Number.isFinite(parsed) ? parsed : undefined;
    } catch (err) {
      totalDuration = undefined;
    }

    const infoToBeSent = {
      name: formState.inputs.name.value,
      school: formState.inputs.school.value,
      driver: formState.inputs.driver.value,
      plate: formState.inputs.plate.value,
      starttime: formState.inputs.starttime.value || "00:00",
      stoptime: formState.inputs.stoptime.value || "23:59",
      student: currentStudents.map((student) => student.id),
      routeClientArrivalTime: CheckTimeString(formState.inputs.transferStartFinishTime.value)
        ? formState.inputs.transferStartFinishTime.value
        : undefined,
      routelocations: routelocations,
      isActive: formState.inputs.isActive.value,
      useUsersDetails: formState.inputs.useUsersDetails.value,
      routeType: formState.inputs.routeType.value,
      // Öğrenci servisi recurring'tir → yapıldığı günler puantaj/scheduler'ı etkiler.
      // Boş → backend hafta içi varsayar (geriye dönük uyum).
      // Demo'da seçici yok; kayıt, rotada duran günleri olduğu gibi bırakır.
      serviceDays: auth.company?.isDemo
        ? Array.isArray(loadedItem?.serviceDays)
          ? loadedItem.serviceDays
          : []
        : Array.isArray(formState.inputs.serviceDays?.value)
          ? formState.inputs.serviceDays.value
          : [],
      ...(totalDistance !== undefined ? { totalDistance } : {}),
      ...(totalDuration !== undefined ? { totalDuration } : {}),
    };

    const selectedSchoolId = formState.inputs.school.value;
    if (selectedSchoolId && currentStudents.length > 0) {
      const mismatchedNames = currentStudents
        .map((current) => {
          const roster = students.find((item) => String(item.id) === String(current.id));
          const studentSchool = roster?.school?.id || roster?.school;
          if (!roster || !studentSchool) return null;
          if (String(studentSchool) === String(selectedSchoolId)) return null;
          return current.name || roster.name;
        })
        .filter(Boolean);
      if (mismatchedNames.length > 0) {
        window.toast.error(t("httpCodes.631", { names: mismatchedNames.join(", ") }));
        return;
      }
    }

    //standard code
    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          setLoadedItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code, { names: err.message }));
      } else if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  });

  const memoizedStops = useMemo(() => {
    return currentStudents.map((student) => ({
      ...student,
      location: checkIsPickUp(formState.inputs.routeType?.value) ? student.locationgeton : student.locationgetoff,
    }));
  }, [currentStudents, formState.inputs.routeType?.value]);

  const memoizedOrigin = useMemo(() => {
    return schools.find((school) => formState.inputs.school.value === school.id);
  }, [schools, formState.inputs.school.value]);

  const memoizedTransferStartFinishTime = useMemo(() => {
    const check = CheckTimeString(formState.inputs.transferStartFinishTime.value);
    if (!check) {
      return null;
    }
    return formState.inputs.transferStartFinishTime.value;
  }, [formState.inputs.transferStartFinishTime.value]);

  const schoolStudentOptions = useMemo(() => {
    const selectedSchoolId = formState.inputs.school.value;
    if (!selectedSchoolId) {
      return [];
    }

    const schoolStudentIds = students
      .filter((student) => student?.school?.toString() === selectedSchoolId.toString())
      .map((student) => student.id);

    return studentOptions.filter((option) => schoolStudentIds.includes(option.value));
  }, [students, studentOptions, formState.inputs.school.value]);

  // Get route type value based on config
  const getRouteTypeValue = () => {
    return formState.inputs.routeType?.value;
  };

  // Get route type options based on config
  const getRouteTypeOptions = () => {
    return currentLanguage === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;
  };

  // Get is active options based on config
  const getIsActiveOptions = () => {
    return currentLanguage === "tr" ? isActiveOptions_tr : isActiveOptions_en;
  };

  // Get yes/no options based on config
  const getYesNoOptions = () => {
    return currentLanguage === "tr" ? yesNoOptions_tr : yesNoOptions_en;
  };

  const vehicleRouteItemTabs = useMemo(() => {
    // Check if any student doesn't have a parent
    const studentsWithoutParents = currentStudents.filter(
      (student) => GetParentFromStudent(student?.id?.toString()) === null,
    );

    // Check if any student doesn't have location information for the active route type
    const studentsWithoutLocation = currentStudents.filter((student) => {
      const location = checkIsPickUp(formState.inputs.routeType?.value)
        ? student?.locationgeton
        : student?.locationgetoff;
      if (!location) return true;
      const { lat, lng } = location;
      return typeof lat !== "number" || typeof lng !== "number" || Number.isNaN(lat) || Number.isNaN(lng);
    });

    const serviceTab = (
      <div>
        <div className="vertical-flex">
          <GenericCard
            title={t("routeInfo")}
            dismissible={false}
            style={{ paddingBottom: "0px", marginBottom: "20px" }}
          >
            <div className="horizontal-flex">
              <div style={{ flex: 2 }}>
                <CustomSelect
                  options={getRouteTypeOptions()}
                  id="routeType"
                  onInput={inputHandler}
                  label={t("routeType")}
                  description={t("routeTypeDescription")}
                  initialValue={
                    getRouteTypeOptions().find(
                      (option) => option.value.toString() === loadedItem?.routeType?.toString(),
                    ) || getRouteTypeOptions()[0]
                  }
                  initialValid={true}
                ></CustomSelect>
              </div>
              {studentSchoolOptions.length > 1 && (
                <div style={{ flex: 3 }}>
                  <CustomSelect
                    options={studentSchoolOptions}
                    id="school"
                    onInput={inputHandler}
                    label={t("client")}
                    initialValue={studentSchoolOptions.find((option) => option.value === loadedItem?.school?.id)}
                    initialValid={isNewItem ? false : true}
                    description={
                      checkIsPickUp(getRouteTypeValue()) ? t("destinationDescription") : t("originDescription")
                    }
                  ></CustomSelect>
                </div>
              )}
              <div style={{ flex: 3 }}>
                <Input
                  id="name"
                  element="input"
                  type="text"
                  label={t("routeName") + " *"}
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText={t("requireField", { item: t("name") })}
                  initialValue={loadedItem?.name}
                  initialValid={isNewItem ? false : true}
                  onInput={inputHandler}
                  description={
                    showPhoneAppAdminUi ? t("routeNameDescription") : undefined
                  }
                />
              </div>

              <div style={{ flex: 2 }}>
                <Input
                  id="transferStartFinishTime"
                  element="input"
                  type="text"
                  label={
                    checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTime")
                      : t("dropOffRouteClientArrivalTime")
                  }
                  description={
                    checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTimeDescription")
                      : t("dropOffRouteClientArrivalTimeDescription")
                  }
                  validators={[VALIDATOR_TIME_EMPTY_ACCEPTED()]}
                  errorText={t("invalidTimeField", {
                    item: checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTime")
                      : t("dropOffRouteClientArrivalTime"),
                  })}
                  onInput={inputHandler}
                  initialValue={
                    CheckTimeString(loadedItem?.routeClientArrivalTime) ? loadedItem?.routeClientArrivalTime : ""
                  }
                  initialValid={true}
                />
              </div>
              {showPhoneAppAdminUi && (
                <React.Fragment>
                  <div style={{ flex: 2 }}>
                    <CustomSelect
                      options={getYesNoOptions()}
                      id="useUsersDetails"
                      onInput={inputHandler}
                      initialValue={
                        getYesNoOptions().find((option) => option.value === loadedItem?.useUsersDetails) ||
                        getYesNoOptions()[1]
                      }
                      initialValid={true}
                      label={t("showPassengersTime")}
                      description={t("showPassengersTimeDescription")}
                    ></CustomSelect>
                  </div>
                  <div style={{ flex: 2 }}>
                    <CustomSelect
                      options={getIsActiveOptions()}
                      id="isActive"
                      label={t("routeStatus")}
                      description={t("routeStatusDescription")}
                      onInput={inputHandler}
                      initialValue={loadedItem?.isActive === false ? getIsActiveOptions()[0] : getIsActiveOptions()[1]}
                      initialValid={true}
                    ></CustomSelect>
                  </div>
                </React.Fragment>
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  alignSelf: "stretch",
                  paddingBottom: "1.3rem",
                }}
              >
                <CreateOppositeServiceButton
                  loadedItem={loadedItem}
                  isNewItem={isNewItem}
                  InsertItem={InsertItem}
                  gotoItem={gotoItem}
                  serviceVariant="vehicleRoute"
                />
              </div>
            </div>
          </GenericCard>

          {!auth.company?.isDemo && (
            <GenericCard title={t("serviceDays.cardTitle")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
              <ServiceDaysPicker
                id="serviceDays"
                onInput={inputHandler}
                initialValue={loadedItem?.serviceDays || []}
                label={t("serviceDays.label")}
              />
            </GenericCard>
          )}

          {!isNewItem && isRideSkipUiEnabled(auth?.company) && (
            <GenericCard title={t("rideSkip.panelTitle")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
              <RideSkipRosterPanel
                serviceKind={RIDE_SKIP_SERVICE_KINDS.VEHICLE_ROUTE}
                serviceId={itemId}
                students={currentStudents}
                timezone={auth?.company?.timezone}
              />
            </GenericCard>
          )}

          <GenericCard title={t("serviceInfo")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <div className="horizontal-flex" style={{ marginBottom: "0px" }}>
              <div style={{ flex: 5.1 }}>
                <CustomSelect
                  options={driverOptions}
                  id="driver"
                  isMulti={true}
                  onInput={inputHandler}
                  label={t("driversForRoute")}
                  initialValue={driverOptions.filter((option) =>
                    currentDrivers.map((driver) => driver.id).includes(option.value),
                  )}
                  initialValid={true}
                  fireChange={(val) => updateDrivers(val)}
                  description={
                    showPhoneAppAdminUi ? t("driversDescription") : undefined
                  }
                ></CustomSelect>
              </div>
              <div style={{ flex: 2 }}>
                <Input
                  id="plate"
                  element="input"
                  type="text"
                  label={t("plateForRoute")}
                  validators={[VALIDATOR_NONE()]}
                  errorText={t("requireField", { item: t("plate") })}
                  onInput={inputHandler}
                  initialValue={loadedItem?.plate}
                  initialValid={isNewItem ? false : true}
                  description={
                    showPhoneAppAdminUi ? t("plateDescription") : undefined
                  }
                />
              </div>

              {showPhoneAppAdminUi && (
                <React.Fragment>
                  <div style={{ flex: 2 }}>
                    <Input
                      id="starttime"
                      element="input"
                      type="text"
                      label={t("serviceObserverStartTime")}
                      validators={[VALIDATOR_TIME_EMPTY_ACCEPTED()]}
                      errorText={t("invalidTimeField", { item: t("serviceObserverStartTime") })}
                      onInput={inputHandler}
                      initialValue={loadedItem?.starttime ? GetStartStopTime(loadedItem?.starttime) : ""}
                      initialValid={true}
                      description={t("serviceObserverStartTimeDescription")}
                    />
                  </div>
                  <div style={{ flex: 2 }}>
                    <Input
                      id="stoptime"
                      element="input"
                      type="text"
                      label={t("serviceObserverEndTime")}
                      validators={[VALIDATOR_TIME_EMPTY_ACCEPTED()]}
                      errorText={t("invalidTimeField", { item: t("serviceObserverEndTime") })}
                      onInput={inputHandler}
                      initialValue={loadedItem?.stoptime ? GetStartStopTime(loadedItem?.stoptime) : ""}
                      initialValid={true}
                      description={t("serviceObserverEndTimeDescription")}
                    />
                  </div>
                </React.Fragment>
              )}
              {!showPhoneAppAdminUi && (
                <div style={{ flex: 10.8 }}>
                  <CustomSelect
                    options={schoolStudentOptions}
                    id="student"
                    isMulti={true}
                    onInput={inputHandler}
                    label={t("passengersIntheRoute")}
                    initialValue={currentStudents.map((student) => ({ value: student.id, label: student.name }))}
                    initialValid={true}
                    fireChange={(val) => updateCurrentStudents(val)}
                    disabled={!formState.inputs.school.value}
                    description={
                      showPhoneAppAdminUi
                        ? t("passengersStudentsDescription")
                        : undefined
                    }
                  ></CustomSelect>
                  {(studentsWithoutParents.length > 0 || studentsWithoutLocation.length > 0) && (
                    <div
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        padding: "12px",
                        marginBottom: "20px",
                        marginTop: "20px",
                        color: "#856404",
                      }}
                    >
                      <strong>{t("warnings")}</strong> <br />
                      {studentsWithoutParents.length > 0 && (
                        <>
                          {t("theseStudentsHasNoParents")}:{" "}
                          {studentsWithoutParents
                            .map((student) => student?.name || student?.fullname || "Bilinmeyen Öğrenci")
                            .join(", ")}
                          <br />
                        </>
                      )}
                      {studentsWithoutLocation.length > 0 && (
                        <>
                          {t("theseStudentsHaveNoLocation")}:{" "}
                          {studentsWithoutLocation
                            .map((student) => student?.name || student?.fullname || "Bilinmeyen Öğrenci")
                            .join(", ")}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {showPhoneAppAdminUi && (
              <div>
                <CustomSelect
                  options={schoolStudentOptions}
                  id="student"
                  isMulti={true}
                  onInput={inputHandler}
                  label={t("passengersIntheRoute")}
                  initialValue={currentStudents.map((student) => ({ value: student.id, label: student.name }))}
                  initialValid={true}
                  fireChange={(val) => updateCurrentStudents(val)}
                  disabled={!formState.inputs.school.value}
                  description={
                    showPhoneAppAdminUi
                      ? t("passengersStudentsDescription")
                      : undefined
                  }
                ></CustomSelect>
                {(studentsWithoutParents.length > 0 || studentsWithoutLocation.length > 0) && (
                  <div
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "4px",
                      padding: "12px",
                      marginBottom: "20px",
                      marginTop: "20px",
                      color: "#856404",
                    }}
                  >
                    <strong>{t("warnings")}</strong> <br />
                    {studentsWithoutParents.length > 0 && (
                      <>
                        {t("theseStudentsHasNoParents")}:{" "}
                        {studentsWithoutParents
                          .map((student) => student?.name || student?.fullname || "Bilinmeyen Öğrenci")
                          .join(", ")}
                        <br />
                      </>
                    )}
                    {studentsWithoutLocation.length > 0 && (
                      <>
                        {t("theseStudentsHaveNoLocation")}:{" "}
                        {studentsWithoutLocation
                          .map((student) => student?.name || student?.fullname || "Bilinmeyen Öğrenci")
                          .join(", ")}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </GenericCard>

          {/* <GenericCard title={t("vehicleAndPassengersInfo")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <div className="horizontal-flex"></div>
          </GenericCard> */}
        </div>

        {currentStudents.length > 0 && (
          <React.Fragment>
            {!forceShowMap && (
              <div
                ref={showMapRef}
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginTop: "10px",
                  marginBottom: "20px",
                  scrollMarginTop: "1rem",
                }}
              >
                <Button type="button" danger size="small" onClick={toggleMap} style={{ minWidth: "200px" }}>
                  {showMap ? t("hideMap") : t("showMap")}
                </Button>
              </div>
            )}

            <div className="divider" />

            {showMap && (
              <div style={{ display: "flex", flexDirection: "row", columnGap: "10px", marginTop: "0px" }}>
                <MapWithTransferTimesContainer
                  stops={memoizedStops}
                  origin={memoizedOrigin}
                  type={getRouteTypeValue()}
                  enablePolyline={true}
                  transferStartFinishTime={memoizedTransferStartFinishTime}
                  isFullScreen={true}
                  savedTotalDistanceKm={loadedItem?.totalDistance}
                  savedTotalDurationMin={loadedItem?.totalDuration}
                  onOptimize={(newStops) => {
                    setCurrentStudents(newStops);
                  }}
                />
              </div>
            )}
          </React.Fragment>
        )}

        <FormFooter
          isNewItem={isNewItem}
          onUpdate={itemUpdateSubmitHandler}
          onDelete={() => setShowConfirmModal(true)}
          isUpdating={isLoading}
          disabled={!formState.inputs.school.value || !formState.isValid || isLoading}
          extraActions={
            !isNewItem && itemId ? (
              <Button type="button" size="small" onClick={() => setShowHistoryModal(true)}>
                {currentLanguage === "tr" ? "Değişiklik Geçmişi" : "Change History"}
              </Button>
            ) : null
          }
        />
      </div>
    );

    // SMS tabını sadece smsCommunication feature'ı aktifse göster
    if (auth?.company?.features?.[COMPANY_FEATURES.SMS_COMMUNICATION]) {
      const smsTab = (
        <SmsTable
          model="vehicleroute"
          users={currentStudents
            .map((student) => GetParentFromStudent(student?.id?.toString()))
            .filter((parent) => Boolean(parent))}
          extraInfo={currentStudents
            .map((student) => {
              const parent = GetParentFromStudent(student?.id?.toString());
              return parent
                ? {
                    userId: parent.id,
                    text: `${t("student.title")}: ${student?.name || student?.fullname || "Bilinmeyen Öğrenci"}`,
                  }
                : null;
            })
            .filter((info) => info !== null)}
          serviceId={itemId}
          driver={currentDrivers.length ? currentDrivers[0] : null}
        />
      );

      return [
        {
          label: t("serviceManagement"),
          content: serviceTab,
        },
        {
          label: t("smsManagement"),
          content: smsTab,
        },
      ];
    }

    // smsCommunication feature'ı aktif değilse, sadece serviceTab döndür (tab yapısı olmadan)
    return serviceTab;
  }, [
    studentSchoolOptions,
    schoolStudentOptions,
    formState.inputs.school.value,
    getRouteTypeValue(),
    formState.inputs.name.value,
    formState.inputs.plate.value,
    formState.inputs.transferStartFinishTime.value,
    driverOptions,
    currentDrivers,
    currentStudents,
    itemId,
    isNewItem,
    loadedItem,
    inputHandler,
    itemUpdateSubmitHandler,
    isLoading,
    t,
    memoizedStops,
    memoizedOrigin,
    memoizedTransferStartFinishTime,
  ]);

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {!isNewItem && itemId && (
        <ServiceAuditHistoryModal
          show={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          entityType="vehicleroute"
          entityId={itemId}
        />
      )}

      {/* Show blocker overlay if trying to create new item but route limit is reached */}
      {shouldBlockNewItem && <RouteLimitBlocker limitStatus={limitStatus} onClose={onClose} isOverlay={true} />}

      {isItemLoaded && (
        <form className={shouldBlockNewItem ? "route-limit-blocked-form" : ""}>
          {auth?.company?.features?.[COMPANY_FEATURES.SMS_COMMUNICATION] ? (
            <Tabs tabs={vehicleRouteItemTabs} tabValue={tabValue} onTabChange={handleTabChange} isVertical={false} />
          ) : (
            vehicleRouteItemTabs
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default VehicleRouteItem;
